import { Button } from "@/components/button/Button";
import { Checkbox } from "@/components/checkbox/Checkbox";
import { HorizontalSeparator } from "@/xpa-components/horizontal-separator/HorizontalSeparator";
import { Input } from "@/components/input/Input";
import type { EmailSignUpForm } from "./EmailSignUp.interface";
import { SnackbarContext } from "@/components/snackbar/Snackbar.context";
import {
  emailPattern,
  FORM_FIELDS,
  passwordPattern,
  SIGN_UP_DEFAULT_VALUES,
} from "@/constants/emailForm.constant";
import { useAuthentication } from "@/context/Authentication";
import { remoteLogger } from "@/utils/remoteLogger";
import { isError } from "@/utils/type-guards/errorTypeGuard";
import classnames from "classnames";
import useTranslation from "next-translate/useTranslation";
import type { VFC } from "react";
import React, { useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styles from "./EmailSignUp.module.scss";
import Trans from "next-translate/Trans";
import { emailAuthService } from "@/services/email-auth-service";
import { SeverityType } from "@/components/snackbar/Snackbar";
import { useLoginRedirectProps } from "@/utils/hooks/useLoginRedirectProps";

const EmailSignUp: VFC = () => {
  const showNotification = useContext(SnackbarContext);
  const methods = useForm<EmailSignUpForm>({
    mode: "onChange",
    criteriaMode: "all",
    defaultValues: SIGN_UP_DEFAULT_VALUES,
    shouldFocusError: true,
  });
  const { t } = useTranslation("web-payments");
  const [passwordView, setPasswordView] = useState(false);
  const { signOut, finishSignUpViaEmail } = useAuthentication();
  const { origin, redirectUrl } = useLoginRedirectProps();

  function handleFailure(err?: unknown, data?: unknown) {
    remoteLogger.error(err, data);
    const message =
      emailAuthService.parseError(err) === "email-already-in-use"
        ? t`LOGIN_FAILED_EMAIL_ALREADY_IN_USE`
        : t`LOGIN_FAILED`;

    showNotification?.({
      message,
      type: SeverityType.error,
    });

    signOut();
  }

  async function handleSubmitData(data: EmailSignUpForm) {
    const { email, password, subscribed, termsAndConditions } = data;

    if (!termsAndConditions) {
      remoteLogger.error(
        "Terms and conditions not accepted. We should not reach this point in code because of the form validation rules",
      );

      return;
    }

    try {
      const user = await emailAuthService.signUp({ email, password });

      finishSignUpViaEmail({
        isConsentProvided: termsAndConditions,
        screen: "sign-up",
        isMarketingEmailsAllowed: subscribed,
        token: user.token,
        redirectUrl,
        origin,
      });
    } catch (err) {
      if (isError(err)) {
        handleFailure(err);
        return;
      }

      handleFailure(
        new Error(`Error in sign up handle submit data ${JSON.stringify(err)}`),
      );
    }
  }

  return (
    <>
      <FormProvider {...methods}>
        <form
          className={styles.form}
          onSubmit={(e) => void methods.handleSubmit(handleSubmitData)(e)}
        >
          <div className={styles.inputWrapper}>
            <Input
              type="email"
              autoComplete="on"
              label={t`LOGIN_EMAIL`}
              {...methods.register(FORM_FIELDS.EMAIL, {
                required: true,
                pattern: emailPattern,
              })}
              error={methods.formState.errors[FORM_FIELDS.EMAIL]}
            />
            {methods.formState.errors[FORM_FIELDS.EMAIL] ? (
              <div className={styles.inputDescription}>
                <strong
                  className={classnames(
                    styles.inputDescriptionError,
                    styles.inputDescriptionText,
                  )}
                >
                  {t`LOGIN_ERROR_EMAIL`}
                </strong>
              </div>
            ) : (
              <div className={styles.inputDescription}>
                <strong className={styles.inputDescriptionText}>
                  {t`LOGIN_EMAIL_DESCRIPTION`}
                </strong>
              </div>
            )}
          </div>

          <div className={styles.inputWrapper}>
            <Input
              label={t`LOGIN_PASSWORD`}
              type={passwordView ? "text" : "password"}
              autoComplete="on"
              {...methods.register(FORM_FIELDS.PASSWORD, {
                required: true,
                pattern: passwordPattern,
              })}
              error={methods.formState.errors[FORM_FIELDS.PASSWORD]}
              className={styles.input}
            />
            {methods.formState.errors[FORM_FIELDS.PASSWORD] && (
              <div className={styles.inputDescription}>
                <strong
                  className={classnames(
                    styles.inputDescriptionError,
                    styles.inputDescriptionText,
                  )}
                >
                  {t`LOGIN_PASSWORD_DESCRIPTION`}
                </strong>
              </div>
            )}
            {!methods.formState.errors[FORM_FIELDS.PASSWORD] && (
              <div className={styles.inputDescription}>
                <p className={styles.inputDescriptionText}>
                  {t`LOGIN_PASSWORD_DESCRIPTION`}
                </p>
              </div>
            )}
          </div>

          <label
            className={classnames(
              styles.checkBoxLabel,
              styles.showPasswordInput,
            )}
          >
            <Checkbox
              onChange={() => {
                setPasswordView(!passwordView);
              }}
            />
            <p className={styles.checkBoxLabelText}>{t`SHOW_PASSWORD`}</p>
          </label>

          <div className={styles.horizontalDivider}>
            <HorizontalSeparator></HorizontalSeparator>
          </div>

          <label
            className={classnames(
              styles.checkBoxLabel,
              styles.termAndConditionsInput,
            )}
          >
            <Checkbox
              {...methods.register(FORM_FIELDS.TERMS_AND_CONDITIONS, {
                required: true,
              })}
            />
            <p className={styles.checkBoxLabelText}>
              <Trans
                i18nKey="web-payments:LOGIN_TERMS_AND_CONDITIONS"
                components={{
                  a1: (
                    <a
                      className={styles.checkBoxLabelTextAnchor}
                      href={t`TERMS_AND_CONDITIONS_LINK`}
                      rel="noreferrer"
                      target="_blank"
                    />
                  ),
                  a2: (
                    <a
                      className={styles.checkBoxLabelTextAnchor}
                      href={t`FOOTER_PRIVACY_LINK`}
                      rel="noreferrer"
                      target="_blank"
                    />
                  ),
                }}
              />
            </p>
          </label>

          <label
            className={classnames(styles.checkBoxLabel, styles.consentInput)}
          >
            <Checkbox {...methods.register(FORM_FIELDS.SUBSCRIBED)} />
            <p className={styles.checkBoxLabelText}>{t`LOGIN_CONSENT`}</p>
          </label>

          <Button
            variant="fill"
            type="submit"
            disabled={
              !methods.formState.isDirty ||
              methods.formState.isSubmitting ||
              !methods.formState.isValid
            }
            className={styles.submitButton}
          >
            <span>{t`LOGIN_CTA_CREATE_ACCOUNT`}</span>
          </Button>
        </form>
      </FormProvider>
    </>
  );
};

export { EmailSignUp };
